import { Injectable } from '@angular/core';

import { PagePath } from '../helpers/config-data-parser';

export type PageActionButtonPath = [navTabNumber: number, pageNumber: number, pageActionButtonCode: string];

export type HtmlTableColumnConfiguration = {
  isRightAligned?: boolean;
}

export type HtmlTableConfiguration = {
  dataColumns?: { [columnIndex: number]: HtmlTableColumnConfiguration };
}

@Injectable({
  providedIn: 'root'
})
export class HtmlHelperService {

  ensurePageActionButtonsAreEnabled(pagePaths: PagePath[], pageActionButtonCodes: string[]): void {
    const pageActionButtonIds = pagePaths
      .map(pagePath => {
        const [navTabNumber, pageNumber] = pagePath;
        return pageActionButtonCodes
          .map(pageActionButtonCode => this.getPageActionButtonId(pageNumber, pageActionButtonCode));
      })
      .flat();
    const distinctPageActionButtonIds = [...new Set(pageActionButtonIds)];
    for (const pageActionButtonId of distinctPageActionButtonIds) {
      const htmlButtonElements = this.getElementsById<HTMLButtonElement>(pageActionButtonId);
      for (const htmlButtonElement of htmlButtonElements ?? []) {
        htmlButtonElement?.removeAttribute('disabled');
      }
    }
  }

  getElementsById<TElement extends Element>(id: string): TElement[] {
    const elements = Array.from<TElement>(document.querySelectorAll(`#${id}`));
    return elements;
  }

  setFocusToHtmlElementByClassName(className: string, delayMs = 0): void {
    this.setFocusToHtmlElement(() => document.getElementsByClassName(className)?.[0] as HTMLElement, delayMs);
  }

  setFocusToHtmlElementById(id: string, delayMs = 0): void {
    this.setFocusToHtmlElement(() => document.getElementById(id), delayMs);
  }

  setFocusToHtmlElement(getHtmlElement: () => HTMLElement, delayMs = 0): void {
    const functionToInvoke = () => {
      const element = getHtmlElement();
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus({ preventScroll: true });
      }
    };
    this.invokeFunction(() => functionToInvoke(), delayMs);
  }

  getPageActionButtonId(pageNumber: number, pageActionButtonCode: string): string {
    const pageActionButtonId = `page${pageNumber}-${pageActionButtonCode}`;
    return pageActionButtonId;
  }

  clickButtonByTitle(appSelector: string, componentSelector: string, buttonTitle: string, delayMs: number = 0): void {
    this.invokeFunction(() => this.clickElement(appSelector, componentSelector, `button[title='${buttonTitle}']`), delayMs);
  }

  hideTableHeadersByContainer(appSelector: string, componentSelector: string, containerClass: string, delayMs: number = 0): void {
    this.invokeFunction(() => {
      const selector = `${appSelector} ${componentSelector} .${containerClass}`;
      const container = document.querySelector(selector);
      if (!container) {
        console.warn(`Container not found with selector: ${selector}`);
        return;
      }

      const tables = container.querySelectorAll<HTMLTableElement>('table');
      tables.forEach(table => {
        const thead = table.querySelector('thead');
        if (thead) {
          thead.style.display = 'none';
        }
      });
    }, delayMs);
  }

  showTableByContainer(appSelector: string, componentSelector: string, containerClass: string, delayMs: number = 200): void {
    this.invokeFunction(() => {
      const selector = `${appSelector} ${componentSelector} .${containerClass}`;
      const container = document.querySelector(selector);
      if (!container) {
        console.warn(`Container not found with selector: ${selector}`);
        return;
      }

      (container as HTMLElement).style.display = '';
    }, delayMs);
  }

  hideTableByContainer(appSelector: string, componentSelector: string, containerClass: string, delayMs: number = 0): void {
    this.invokeFunction(() => {
      const selector = `${appSelector} ${componentSelector} .${containerClass}`;
      const container = document.querySelector(selector);
      if (!container) {
        console.warn(`Container not found with selector: ${selector}`);
        return;
      }

      (container as HTMLElement).style.display = 'none';
    }, delayMs);
  }

  downloadBlob(blob: Blob, fileName: string): void {

    const blobUrl = URL.createObjectURL(blob);

    const hiddenAnchor = document.createElement('a');
    hiddenAnchor.style.display = 'none';
    hiddenAnchor.href = blobUrl;
    hiddenAnchor.download = fileName;

    document.body.appendChild(hiddenAnchor);
    hiddenAnchor.click();
    document.body.removeChild(hiddenAnchor);

    URL.revokeObjectURL(blobUrl);
  }

  getTableInputList(
    appSelector: string,
    componentSelector: string,
    inputType: string): NodeListOf<HTMLInputElement> {

    return document.querySelectorAll(
      `${appSelector} ${componentSelector}  p-table tbody input[type="${inputType}"]`
    );
  }

  setSpinnerElementContent(spinnerSelector: string, htmlContent: string): void {
    const spinnerElements = this.getSpinnerElements(spinnerSelector);
    spinnerElements.forEach(spinnerElement => spinnerElement.innerHTML = htmlContent);
  }

  getSpinnerElements(spinnerSelector: string): NodeListOf<HTMLElement> {
    const spinnerElements = document.querySelectorAll<HTMLElement>(spinnerSelector);
    return spinnerElements;
  }

  isSpinnerVisible(spinnerSelector: string): boolean {
    const spinnerElements = document.querySelectorAll<HTMLElement>(spinnerSelector);
    return spinnerElements.length > 0;
  }

  private invokeFunction(functionToInvoke: () => void, delayMs: number = null): void {
    if (delayMs !== null && delayMs !== undefined && delayMs >= 0) {
      setTimeout(() => functionToInvoke(), delayMs);
    } else {
      functionToInvoke();
    }
  }

  private clickElement(appSelector: string, componentSelector: string, elementSelector: string): void {
    const element = document.querySelector(`${appSelector} ${componentSelector} ${elementSelector}`) as HTMLElement;
    element?.click();
  }
}
